import { z } from "zod";

/**
 * How the Windows sync agent delivers an integration's query results.
 *
 * - "api" POSTs rows to /sync and lets the backend derive the cursor from them.
 * - "s3"  writes one CSV per run straight to S3 and reports the cursor separately.
 */
export const RELAY_DELIVERY_TARGETS = ["api", "s3"] as const;

export const RelayDeliveryTargetSchema = z.enum(RELAY_DELIVERY_TARGETS);

export type RelayDeliveryTarget = z.infer<typeof RelayDeliveryTargetSchema>;

/**
 * An integration's delivery target, where null means "inherit the agent-wide
 * setting in appsettings.json".
 *
 * Inherit is the default, and deliberately not just a nicety. The agent-wide
 * switch stays the one-action kill switch for a whole box: if a bucket or the
 * ingest breaks mid-migration, an operator flips that machine back to "api"
 * without touching N integration rows. Rows that opted in explicitly keep their
 * choice and are unaffected by the fallback moving.
 */
export const RelayDeliveryTargetOverrideSchema =
  RelayDeliveryTargetSchema.nullable();

export type RelayDeliveryTargetOverride = z.infer<
  typeof RelayDeliveryTargetOverrideSchema
>;

/** Sentinel the UI uses for the null (inherit) option, since a Select needs a value. */
export const RELAY_DELIVERY_TARGET_INHERIT = "inherit";

/**
 * Resolves the target actually used for a run.
 *
 * The single place the precedence rule lives. Both the ERP (to show an operator
 * what will happen) and the agent (to decide what does happen) answer this
 * question, and they must answer it identically — a disagreement here sends rows
 * somewhere the other side isn't looking.
 */
export function resolveRelayDeliveryTarget(
  integrationTarget: RelayDeliveryTarget | null | undefined,
  agentTarget: RelayDeliveryTarget | null | undefined,
): RelayDeliveryTarget {
  return integrationTarget ?? agentTarget ?? "api";
}

/** True when a value is a delivery target the agent understands. */
export function isRelayDeliveryTarget(
  value: unknown,
): value is RelayDeliveryTarget {
  return RelayDeliveryTargetSchema.safeParse(value).success;
}
